import { AUTHENTICATED } from '@mkcore/platform/schema';
import { CsvEnum, Id, IsoDate, IsoDateTime, ListQuery, Paginated, QueryVersion, defineAction, defineEvent, z } from '@mkcore/shared';
import { devPermissions as D, feedbackPermissions as P } from './permissions';

// Namespace dev, resource feedback (CO-0398): bugs, feature requests and questions reported from the app, kept as a
// local copy of the Forgejo issue they become (ADR-0003: Forgejo is the source of truth, the app is the workplace).

export const FEEDBACK_KINDS = ['bug', 'feature', 'question'] as const;
export const FEEDBACK_SEVERITIES = ['critical', 'major', 'minor'] as const;
export const FEEDBACK_STATUSES = ['new', 'triaged', 'in_progress', 'resolved', 'closed'] as const;
export const OPEN_FEEDBACK_STATUSES = ['new', 'triaged', 'in_progress'] as const;
/** The packages of docs/01-architectuur/03-bouwstenen.md, as the area:<package> labels of the werkwijze. */
export const FEEDBACK_AREAS = [
  'platform',
  'crm',
  'engagements',
  'projects',
  'time',
  'billing',
  'dev',
  'reporting',
  'proposals',
  'support',
  'portal',
  'files',
  'collab',
  'ai',
  'approvals',
  'knowledge',
  'agents',
  'other',
] as const;
export const MAX_SCREENSHOT_BYTES = 8 * 1024 * 1024;
export const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

export const FeedbackKind = z.enum(FEEDBACK_KINDS);
export const FeedbackSeverity = z.enum(FEEDBACK_SEVERITIES);
export const FeedbackStatus = z.enum(FEEDBACK_STATUSES);
export const FeedbackArea = z.enum(FEEDBACK_AREAS);
export const FeedbackSource = z.enum(['app', 'forge']);
const ExpectedVersion = z.number().int().positive().optional().describe('Version you read; the update fails if it changed');

/** Browser, screen, language and versions at the time of the report; only scalars. */
export const FeedbackContext = z.record(z.string().max(60), z.union([z.string().max(1000), z.number(), z.boolean()]));
export const ConsoleEntry = z.object({
  t: z.string().max(40),
  level: z.enum(['error', 'warn', 'info', 'log']),
  source: z.string().max(20).optional(),
  message: z.string().max(2000),
});
export const SessionEntry = z.object({
  t: z.string().max(40),
  kind: z.enum(['nav', 'api', 'click', 'error']),
  path: z.string().max(500).optional(),
  method: z.string().max(10).optional(),
  status: z.number().int().optional(),
  ms: z.number().int().optional(),
  label: z.string().max(200).optional(),
});
export type ConsoleEntry = z.infer<typeof ConsoleEntry>;
export type SessionEntry = z.infer<typeof SessionEntry>;
export type FeedbackContext = z.infer<typeof FeedbackContext>;

export const FeedbackForge = z
  .object({
    number: z.number().int().nullable().describe('The issue number, once the report is in Forgejo'),
    url: z.string().nullable(),
    state: z.enum(['open', 'closed']).nullable(),
    labels: z.array(z.string()),
    comments: z.number().int(),
    author: z.string().nullable(),
    syncedAt: IsoDateTime.nullable(),
    error: z.string().nullable().describe('Why the last attempt to send or read back failed'),
    warning: z.string().nullable().describe('For example labels the repository does not have'),
    attempts: z.number().int(),
  })
  .openapi('FeedbackForge');

export const FeedbackComment = z
  .object({
    id: Id,
    externalId: z.number().int(),
    author: z.string(),
    body: z.string(),
    url: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .openapi('FeedbackComment');

const feedbackFields = {
  id: Id,
  number: z.number().int(),
  ref: z.string().describe('FB-<number>, the reference people use'),
  source: FeedbackSource.describe('app: reported here; forge: filed in Forgejo directly and brought in'),
  kind: FeedbackKind,
  title: z.string(),
  description: z.string(),
  repro: z.string().nullable(),
  expected: z.string().nullable(),
  actual: z.string().nullable(),
  severity: FeedbackSeverity.nullable(),
  area: FeedbackArea,
  page: z.string(),
  status: FeedbackStatus,
  reporterId: Id.nullable(),
  reporterLabel: z.string().describe('Name and e-mail at the time, or the Forgejo account'),
  hasScreenshot: z.boolean(),
  triageNote: z.string().nullable(),
  forge: FeedbackForge,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  version: z.number().int(),
  url: z.string().describe('Deep link to the report in the web app'),
};

export const FeedbackListItem = z.object(feedbackFields).openapi('FeedbackListItem');
export const Feedback = z
  .object({
    ...feedbackFields,
    context: FeedbackContext,
    consoleLog: z.array(ConsoleEntry),
    sessionLog: z.array(SessionEntry),
    comments: z.array(FeedbackComment).describe('Comments on the Forgejo issue, mirrored'),
  })
  .openapi('Feedback');

export const createFeedback = defineAction({
  name: 'dev.feedback.create',
  method: 'post',
  path: '/dev/feedback',
  permission: P.keys.create,
  input: z
    .object({
      kind: FeedbackKind,
      title: z.string().min(3).max(200),
      description: z.string().max(20_000).default(''),
      repro: z.string().max(20_000).optional().describe('Bug: what you did'),
      expected: z.string().max(20_000).optional().describe('Bug: what you expected'),
      actual: z.string().max(20_000).optional().describe('Bug: what happened instead'),
      severity: FeedbackSeverity.optional().describe('Bug only; default minor'),
      area: FeedbackArea.default('other'),
      page: z.string().max(500).default('').describe('The path in the app where it happened'),
      includeDetails: z.boolean().default(true).describe('Whether context, console and session logs are included'),
      withScreenshot: z.boolean().default(false).describe('A screenshot follows with PUT /dev/feedback/{id}/screenshot; the job waits for it briefly'),
      context: FeedbackContext.optional(),
      consoleLog: z.array(ConsoleEntry).max(200).optional(),
      sessionLog: z.array(SessionEntry).max(250).optional(),
    })
    .strict(),
  output: Feedback,
  summary: 'Report a bug, a feature request or a question; it becomes a Forgejo issue',
  description: 'The report is saved at once and sent to Forgejo by a job. Add a screenshot afterwards with PUT /dev/feedback/{id}/screenshot (REST only).',
});

export const listFeedback = defineAction({
  name: 'dev.feedback.list',
  method: 'get',
  path: '/dev/feedback',
  permission: AUTHENTICATED,
  input: ListQuery.extend({
    scope: z.enum(['mine', 'all']).optional().describe('all needs feedback:read; without it you see your own reports'),
    status: CsvEnum([...FEEDBACK_STATUSES, 'open']).optional().describe('open = new, triaged and in_progress'),
    kind: CsvEnum(FEEDBACK_KINDS).optional(),
    severity: CsvEnum(FEEDBACK_SEVERITIES).optional(),
    area: CsvEnum(FEEDBACK_AREAS).optional(),
    source: CsvEnum(['app', 'forge']).optional(),
    q: z.string().max(200).optional().describe('Search in title, description, reporter, FB or issue number'),
  }),
  output: Paginated(FeedbackListItem),
  summary: 'List feedback, newest first',
});

export const getFeedback = defineAction({
  name: 'dev.feedback.get',
  method: 'get',
  path: '/dev/feedback/{id}',
  permission: AUTHENTICATED,
  input: z.object({ id: Id }),
  output: Feedback,
  etag: true,
  summary: 'One report with its logs and the comments on its issue; your own, or any with feedback:read',
});

export const updateFeedback = defineAction({
  name: 'dev.feedback.update',
  method: 'patch',
  path: '/dev/feedback/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      title: z.string().min(3).max(200).optional(),
      kind: FeedbackKind.optional(),
      severity: FeedbackSeverity.nullable().optional(),
      area: FeedbackArea.optional(),
      status: FeedbackStatus.optional(),
      triageNote: z.string().max(20_000).nullable().optional().describe('Kept in the app only'),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: Feedback,
  etag: true,
  summary: 'Triage: status, kind, severity, area, title or note; written to the Forgejo issue first',
  description: 'When the report is in Forgejo, state, title and labels change there first; if Forgejo refuses, nothing changes here.',
});

export const deleteFeedback = defineAction({
  name: 'dev.feedback.delete',
  method: 'delete',
  path: '/dev/feedback/{id}',
  permission: P.keys.delete,
  input: z.object({ id: Id, expectedVersion: QueryVersion }),
  output: z.object({ ok: z.literal(true) }),
  etag: true,
  kind: 'destructive',
  summary: 'Delete a report that is not in Forgejo yet; a tracked report is closed in Forgejo instead',
});

export const sendFeedback = defineAction({
  name: 'dev.feedback.send',
  method: 'post',
  path: '/dev/feedback/{id}/send',
  permission: P.keys.update,
  input: z.object({ id: Id }),
  output: Feedback,
  successStatus: 200,
  kind: 'external',
  summary: 'Send a report to Forgejo now, or read its issue back when it has one',
  description: 'Fails with upstream-failed when Forgejo cannot be reached; the error stays on the report.',
});

export const FeedbackSummary = z
  .object({
    open: z.object({ bug: z.number().int(), feature: z.number().int(), question: z.number().int() }),
    openBugsBySeverity: z.object({ critical: z.number().int(), major: z.number().int(), minor: z.number().int() }),
    totalOpen: z.number().int(),
    notInForge: z.number().int().describe('Reports without an issue yet'),
  })
  .openapi('FeedbackSummary');

export const feedbackSummary = defineAction({
  name: 'dev.feedback.summary',
  method: 'get',
  path: '/dev/feedback-summary',
  permission: P.keys.read,
  input: z.object({}),
  output: FeedbackSummary,
  summary: 'What is open, per kind and per severity, from the local copy',
});

export const FeedbackConfig = z
  .object({
    areas: z.array(FeedbackArea),
    maxScreenshotBytes: z.number().int(),
    forge: z
      .object({ configured: z.boolean(), repoUrl: z.string().nullable(), labels: z.array(z.string()).describe('Labels the app puts on issues') })
      .openapi('FeedbackForgeConfig'),
    canManage: z.boolean().describe('Whether the caller may see all feedback and triage it'),
  })
  .openapi('FeedbackConfig');

export const feedbackConfig = defineAction({
  name: 'dev.feedback.config',
  method: 'get',
  path: '/dev/feedback-config',
  permission: AUTHENTICATED,
  input: z.object({}),
  output: FeedbackConfig,
  summary: 'What the feedback form needs: areas, limits and whether Forgejo is connected',
});

export const FeedbackSyncResult = z
  .object({
    imported: z.number().int().describe('Open issues filed in Forgejo that were brought in'),
    checked: z.number().int().describe('Open reports read back from Forgejo'),
    resolved: z.number().int().describe('Reports that turned out closed in Forgejo'),
    warning: z.string().nullable(),
  })
  .openapi('FeedbackSyncResult');

export const syncFeedback = defineAction({
  name: 'dev.feedback.sync',
  method: 'post',
  path: '/dev/feedback-sync',
  permission: P.keys.update,
  input: z.object({}),
  output: FeedbackSyncResult,
  successStatus: 200,
  kind: 'external',
  summary: 'Bring in issues filed in Forgejo and read back every open report; the hourly job does the same',
});

// ---------- events ----------

export const feedbackCreated = defineEvent({
  type: 'dev.feedback.created',
  version: 1,
  payload: z.object({ feedbackId: Id }),
});

export const feedbackChanged = defineEvent({
  type: 'dev.feedback.changed',
  version: 1,
  payload: z.object({
    feedbackId: Id,
    change: z.enum(['updated', 'sent', 'synced', 'screenshot', 'deleted']),
  }),
});

export const feedbackActions = [
  createFeedback,
  listFeedback,
  getFeedback,
  updateFeedback,
  deleteFeedback,
  sendFeedback,
  feedbackSummary,
  feedbackConfig,
  syncFeedback,
];
export const feedbackEvents = [feedbackCreated, feedbackChanged];

// ---------- the issue mirror (U2, ADR-0003) ----------
// Namespace dev: forge organisations and repositories (which are mirrored), their sync state, and the mirrored
// issues, comments, labels and milestones. Reading works on the mirror; the forge is only asked by the sync.

/** A comma separated list in a query string (REST) or an array (MCP). */
const CsvStrings = z.preprocess(
  (v) => (typeof v === 'string' ? v.split(',').map((x) => x.trim()).filter(Boolean) : v),
  z.array(z.string().min(1).max(200)).max(20),
);

export const SYNC_PHASES = ['new', 'backfill', 'ready', 'stopped'] as const;
export const SYNC_STATUSES = ['ok', 'error', 'uncertain'] as const;
export const SyncState = z
  .object({
    phase: z.enum(SYNC_PHASES).describe('new: not synced yet; backfill: first full read; ready: kept up to date; stopped: mirror archived'),
    status: z.enum(SYNC_STATUSES).describe('uncertain until a sync finished without error; counters then show the last known values'),
    backfillStep: z.enum(['labels', 'issues', 'comments', 'pulls', 'commits', 'done']),
    backfillPage: z.number().int(),
    backfillStartedAt: IsoDateTime.nullable(),
    backfillDoneAt: IsoDateTime.nullable(),
    cursor: IsoDateTime.nullable().describe('Changes since this moment are read by the next reconciliation'),
    lastReconcileAt: IsoDateTime.nullable(),
    lastFullAt: IsoDateTime.nullable(),
    lastWebhookAt: IsoDateTime.nullable(),
    error: z.string().nullable(),
    errorAt: IsoDateTime.nullable(),
    counts: z.object({ issues: z.number().int(), pulls: z.number().int(), comments: z.number().int(), labels: z.number().int(), milestones: z.number().int(), commits: z.number().int() }).partial(),
  })
  .openapi('SyncState');

export const ForgeOrg = z
  .object({
    id: Id,
    login: z.string(),
    organisationId: Id.nullable().describe('The CRM client this organisation works for; null for our own'),
    enabled: z.boolean(),
    webhookUrl: z.string().describe('Register this URL as the organisation webhook in the forge, signed with the secret forgejo.webhook'),
    lastWebhookAt: IsoDateTime.nullable(),
    hookId: z.number().int().nullable().describe('The organisation webhook in Forgejo, once registered from the app'),
    hookRegisteredAt: IsoDateTime.nullable(),
    repositories: z.number().int(),
    mirrored: z.number().int(),
    version: z.number().int(),
  })
  .openapi('ForgeOrg');

export const Repository = z
  .object({
    id: Id,
    forgeOrgId: Id,
    owner: z.string(),
    name: z.string(),
    fullName: z.string(),
    url: z.string(),
    defaultBranch: z.string().nullable(),
    private: z.boolean(),
    description: z.string(),
    enabled: z.boolean().describe('Mirrored; turning it on starts the backfill, off archives the mirror'),
    archivedAt: IsoDateTime.nullable(),
    organisationId: Id.nullable(),
    projectId: Id.nullable(),
    editorNames: z.array(z.string()).describe('Other names an editor gives this repository (the folder of a checkout), for editor activity'),
    sync: SyncState.nullable(),
    version: z.number().int(),
  })
  .openapi('Repository');

/** A name an editor gives a repository (CO-0260): a folder or project name, compared without case. */
const EditorName = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .regex(/^[^\s/]+$/, 'One name, without spaces or a slash')
  .transform((s) => s.toLowerCase());

const ForgeLogin = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9._-]+$/, 'A forge organisation or user name');

export const mirrorStatus = defineAction({
  name: 'dev.mirror.status',
  method: 'get',
  path: '/dev/mirror-status',
  permission: D.keys.read,
  input: z.object({}),
  output: z.object({
    configured: z.boolean(),
    forgeUrl: z.string().nullable(),
    syncTokenSet: z.boolean().describe('The read-only token is the tenant-wide secret forgejo.syncToken'),
    webhookSecretSet: z.boolean().describe('The tenant-wide secret forgejo.webhook signs the organisation webhooks'),
  }),
  summary: 'Whether the mirror can run: the forge address and the secrets it needs',
});

export const listForgeOrgs = defineAction({
  name: 'dev.forgeOrg.list',
  method: 'get',
  path: '/dev/forge-orgs',
  permission: D.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(ForgeOrg) }),
});

export const createForgeOrg = defineAction({
  name: 'dev.forgeOrg.create',
  method: 'post',
  path: '/dev/forge-orgs',
  permission: D.keys.manage,
  input: z.object({ login: ForgeLogin, organisationId: Id.nullable().default(null) }).strict(),
  output: ForgeOrg,
  summary: 'Add a forge organisation or user whose repositories may be mirrored; then run dev.forgeOrg.discover',
});

export const updateForgeOrg = defineAction({
  name: 'dev.forgeOrg.update',
  method: 'patch',
  path: '/dev/forge-orgs/{id}',
  permission: D.keys.manage,
  input: z.object({ id: Id, organisationId: Id.nullable().optional(), enabled: z.boolean().optional(), expectedVersion: z.number().int().positive().optional() }).strict(),
  output: ForgeOrg,
  etag: true,
});

export const deleteForgeOrg = defineAction({
  name: 'dev.forgeOrg.delete',
  method: 'delete',
  path: '/dev/forge-orgs/{id}',
  permission: D.keys.manage,
  input: z.object({ id: Id, expectedVersion: QueryVersion }).strict(),
  output: z.object({ id: Id, deleted: z.literal(true) }),
  etag: true,
  summary: 'Remove an organisation with its repositories and their mirror from the app; the forge is not touched',
});

export const registerForgeOrgHook = defineAction({
  name: 'dev.forgeOrg.registerHook',
  method: 'post',
  path: '/dev/forge-orgs/{id}/hook',
  permission: D.keys.manage,
  input: z.object({ id: Id }).strict(),
  output: ForgeOrg,
  successStatus: 200,
  summary: 'Registers the organisation webhook of the mirror in Forgejo with your linked account, or updates it (CO-0763)',
  description: 'Needs the secret forgejo.webhook and an account that may manage the hooks of the organisation in Forgejo. Running it again is harmless.',
});

export const discoverRepositories = defineAction({
  name: 'dev.forgeOrg.discover',
  method: 'post',
  path: '/dev/forge-orgs/{id}/discover',
  permission: D.keys.manage,
  input: z.object({ id: Id }).strict(),
  output: z.object({ found: z.number().int(), added: z.number().int(), items: z.array(Repository) }),
  successStatus: 200,
  summary: 'Read the repositories of the organisation from the forge; new ones are added switched off',
});

export const listRepositories = defineAction({
  name: 'dev.repo.list',
  method: 'get',
  path: '/dev/repositories',
  permission: D.keys.read,
  input: z.object({ forgeOrgId: Id.optional(), enabled: z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean()).optional() }),
  output: z.object({ items: z.array(Repository) }),
  summary: 'The repositories with their sync state',
});

export const updateRepository = defineAction({
  name: 'dev.repo.update',
  method: 'patch',
  path: '/dev/repositories/{id}',
  permission: D.keys.manage,
  input: z
    .object({
      id: Id,
      enabled: z.boolean().optional().describe('On starts the backfill; off stops the sync and archives the mirror'),
      projectId: Id.nullable().optional(),
      organisationId: Id.nullable().optional(),
      editorNames: z
        .array(EditorName)
        .max(20)
        .optional()
        .describe('Replaces the other names an editor gives this repository, such as havep-erp-rebuild for VPT/VPTCore; editor activity under such a name counts for this repository'),
      expectedVersion: z.number().int().positive().optional(),
    })
    .strict(),
  output: Repository,
  etag: true,
});

export const syncRepository = defineAction({
  name: 'dev.repo.sync',
  method: 'post',
  path: '/dev/repositories/{id}/sync',
  permission: D.keys.manage,
  input: z.object({ id: Id, full: z.boolean().default(false).describe('Compare everything with the forge, as the nightly run does') }).strict(),
  output: z.object({ workflowId: z.string() }),
  successStatus: 202,
  summary: 'Read the changes of a mirrored repository now; follow the run with admin.job.get',
});

export const ISSUE_STATES = ['open', 'closed', 'all'] as const;
export const Issue = z
  .object({
    id: Id,
    repositoryId: Id,
    repository: z.string().describe('owner/name'),
    projectId: Id.nullable().describe('The project the repository is linked to; time on the issue goes there'),
    number: z.number().int(),
    title: z.string(),
    state: z.enum(['open', 'closed']),
    labels: z.array(z.string()),
    milestone: z.object({ id: Id, title: z.string() }).nullable(),
    assignees: z.array(z.string()),
    author: z.string().nullable(),
    commentCount: z.number().int(),
    url: z.string(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    closedAt: IsoDateTime.nullable(),
    estimateMinutes: z.number().int().nullable().describe('App field: the estimate in minutes (CO-0275); compare with time.issue.summary'),
    agentId: Id.nullable().describe('App field: the agent working on the issue (CO-1236); its runs are agent.run.list with targetId'),
    clientVisible: z.boolean().describe('App field: the client sees it in the portal (CO-0665)'),
  })
  .openapi('Issue');

export const IssueComment = z
  .object({ id: Id, author: z.string().nullable(), body: z.string(), url: z.string().nullable(), createdAt: IsoDateTime, updatedAt: IsoDateTime })
  .openapi('IssueComment');

export const CI_STATES = ['pending', 'success', 'failure', 'error'] as const;

/** A pull request or commit that refers to an issue (CO-0419): closes (Closes #12, Fixes #12) or mentions (#12). */
export const IssueLink = z
  .object({
    kind: z.enum(['closes', 'mentions']),
    type: z.enum(['pull', 'commit']),
    id: Id,
    /** #number of a pull request, or the short sha of a commit. */
    ref: z.string(),
    title: z.string(),
    state: z.enum(['open', 'closed', 'merged']).nullable(),
    ciState: z.enum(CI_STATES).nullable(),
    url: z.string(),
    at: IsoDateTime,
  })
  .openapi('IssueLink');

/** An issue on the other side of a dependency (CO-0399); id is null when it lives outside the mirror. */
export const DependencyRef = z
  .object({ id: Id.nullable(), ref: z.string().describe('owner/repo#12'), title: z.string(), state: z.enum(['open', 'closed']).nullable() })
  .openapi('DependencyRef');

export const IssueDetail = Issue.extend({
  body: z.string(),
  comments: z.array(IssueComment),
  links: z.array(IssueLink),
  blockedBy: z.array(DependencyRef).describe('Issues that must be closed first'),
  blocks: z.array(DependencyRef).describe('Mirrored issues this one blocks'),
  syncedAt: IsoDateTime,
  suggestions: z
    .record(z.string(), z.object({ value: z.unknown(), label: z.string().nullable(), confidence: z.number(), reason: z.string(), by: z.string().nullable(), at: IsoDateTime }))
    .describe('Proposals for labels and milestone that wait for a person (CO-0845)'),
}).openapi('IssueDetail');

/** Fields an advanced issue filter can test (CO-0404). */
export const FILTER_FIELDS = ['label', 'assignee', 'author', 'milestone', 'state', 'repository', 'text'] as const;
export const FILTER_MAX_CONDITIONS = 20;

export const FilterCondition = z.object({
  field: z.enum(FILTER_FIELDS),
  value: z.string().min(1).max(200).describe('A label name, user, milestone id, state (open or closed), repository id or words'),
  not: z.boolean().optional().describe('Match issues that do not satisfy this condition'),
});
export type FilterCondition = z.infer<typeof FilterCondition>;
export type FilterGroup = { op: 'and' | 'or'; items: (FilterCondition | FilterGroup)[] };
// Not recursive on purpose: the OpenAPI and MCP schemas cannot describe a recursive type, so the depth is spelled out.
const group = <T extends z.ZodType>(inner: T) => z.object({ op: z.enum(['and', 'or']), items: z.array(z.union([FilterCondition, inner])).min(1).max(FILTER_MAX_CONDITIONS) });
const FilterGroup3 = z.object({ op: z.enum(['and', 'or']), items: z.array(FilterCondition).min(1).max(FILTER_MAX_CONDITIONS) });
export const FilterGroup = group(group(FilterGroup3)) as unknown as z.ZodType<FilterGroup>;

const filterConditions = (g: FilterGroup): number => g.items.reduce((n, i) => n + ('op' in i ? filterConditions(i) : 1), 0);

/** A condition tree with AND/OR groups, as JSON in a query string (REST) or an object (MCP). */
const IssueFilter = z
  .preprocess((v) => {
    if (typeof v !== 'string') return v;
    try {
      return JSON.parse(v);
    } catch {
      return v;
    }
  }, FilterGroup)
  .refine((g) => filterConditions(g) <= FILTER_MAX_CONDITIONS, `At most ${FILTER_MAX_CONDITIONS} conditions`);

export const listIssues = defineAction({
  name: 'dev.issue.list',
  method: 'get',
  path: '/dev/issues',
  permission: D.keys.read,
  input: ListQuery.extend({
    repositoryId: Id.optional(),
    forgeOrgId: Id.optional(),
    organisationId: Id.optional().describe('Issues of the repositories of this client'),
    projectId: Id.optional().describe('Issues of the repositories linked to this project'),
    state: z.enum(ISSUE_STATES).default('open'),
    labels: CsvStrings.optional().describe('All of these labels'),
    milestoneId: Id.optional(),
    assignee: z.string().max(100).optional(),
    author: z.string().max(100).optional(),
    q: z.string().max(200).optional().describe('Words in title or body, or #number'),
    filter: IssueFilter.optional().describe('Advanced filter: {op: and|or, items: [{field, value, not?} | group]}, combined with the other filters (CO-0404)'),
    sort: z.enum(['updated', 'created', 'number']).default('updated'),
  }),
  output: Paginated(Issue),
  summary: 'Mirrored issues over all repositories, newest change first; filter on repository, client, project, state, labels, milestone, people and words',
});

export const getIssue = defineAction({
  name: 'dev.issue.get',
  method: 'get',
  path: '/dev/issues/{id}',
  permission: D.keys.read,
  input: z.object({ id: Id }),
  output: IssueDetail,
});

export const Label = z
  .object({ id: Id, forgeOrgId: Id, repositoryId: Id.nullable(), name: z.string(), group: z.string().nullable(), color: z.string(), description: z.string(), exclusive: z.boolean() })
  .openapi('Label');

export const listLabels = defineAction({
  name: 'dev.label.list',
  method: 'get',
  path: '/dev/labels',
  permission: D.keys.read,
  input: z.object({ repositoryId: Id.optional(), forgeOrgId: Id.optional() }),
  output: z.object({ items: z.array(Label) }),
  summary: 'The labels of a repository and its organisation, with their group',
});

export const LabelGroup = z
  .object({ name: z.string(), title: z.string(), writable: z.boolean().describe('A board may change labels of this group'), position: z.number().int(), labels: z.number().int() })
  .openapi('LabelGroup');

export const listLabelGroups = defineAction({
  name: 'dev.labelGroup.list',
  method: 'get',
  path: '/dev/label-groups',
  permission: D.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(LabelGroup) }),
  summary: 'Label groups (the part before "/" of a scoped label) and whether a board may change them',
});

export const updateLabelGroup = defineAction({
  name: 'dev.labelGroup.update',
  method: 'put',
  path: '/dev/label-groups/{name}',
  permission: D.keys.manage,
  input: z
    .object({
      name: z.string().min(1).max(60).regex(/^[a-z0-9_-]+$/),
      title: z.string().max(100).optional(),
      writable: z.boolean().optional(),
      position: z.number().int().min(0).max(1000).optional(),
    })
    .strict(),
  output: LabelGroup,
  successStatus: 200,
});

export const Milestone = z
  .object({
    id: Id,
    repositoryId: Id,
    title: z.string(),
    description: z.string(),
    state: z.enum(['open', 'closed']),
    dueOn: IsoDateTime.nullable(),
    openIssues: z.number().int(),
    closedIssues: z.number().int(),
    progress: z.number().describe('Share of closed issues, 0 to 1, from the mirror'),
  })
  .openapi('Milestone');

export const listMilestones = defineAction({
  name: 'dev.milestone.list',
  method: 'get',
  path: '/dev/milestones',
  permission: D.keys.read,
  input: z.object({ repositoryId: Id.optional(), state: z.enum(ISSUE_STATES).default('open') }),
  output: z.object({ items: z.array(Milestone) }),
  summary: 'Milestones with due date and progress from the mirrored issues',
});

// ---------------------------------------------------------------- write-through (CO-0390, CO-0394, CO-0388, CO-0391)

const WRITE_NOTE =
  'Written to Forgejo with your linked account first (Settings, Connections), then taken over by the mirror. For an agent it is a draft: the call waits for a person (approval-pending), who approves it with their own account';
const Login = z.string().min(1).max(100);
const LabelNames = z.array(z.string().min(1).max(100)).max(50);

export const createIssue = defineAction({
  name: 'dev.issue.create',
  method: 'post',
  path: '/dev/issues',
  permission: D.keys.create,
  input: z
    .object({
      repositoryId: Id,
      title: z.string().trim().min(1).max(255),
      body: z.string().max(65_000).default('').describe('Markdown'),
      labels: LabelNames.default([]).describe('Label names of the repository or its organisation'),
      milestoneId: Id.nullable().default(null),
      assignees: z.array(Login).max(10).default([]).describe('Forgejo logins'),
    })
    .strict(),
  output: IssueDetail,
  kind: 'external',
  summary: `Creates an issue in a mirrored repository. ${WRITE_NOTE}`,
});

export const updateIssue = defineAction({
  name: 'dev.issue.update',
  method: 'patch',
  path: '/dev/issues/{id}',
  permission: D.keys.update,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      title: z.string().trim().min(1).max(255).optional(),
      body: z.string().max(65_000).optional().describe('Markdown'),
      state: z.enum(['open', 'closed']).optional().describe('closed closes the issue, open reopens it'),
      milestoneId: Id.nullable().optional().describe('null removes the milestone'),
      assignees: z.array(Login).max(10).optional().describe('The full list of assignees'),
    })
    .strict(),
  output: IssueDetail,
  kind: 'external',
  summary: `Changes title, body, state, milestone or assignees of an issue. ${WRITE_NOTE}`,
});

export const labelIssue = defineAction({
  name: 'dev.issue.label',
  method: 'post',
  path: '/dev/issues/{id}/labels',
  permission: D.keys.update,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      add: LabelNames.default([]).describe('Label names to add; an exclusive scoped label replaces its sibling'),
      remove: LabelNames.default([]).describe('Label names to remove'),
    })
    .strict(),
  output: IssueDetail,
  kind: 'external',
  summary: `Adds and removes labels of an issue. ${WRITE_NOTE}`,
});

export const commentIssue = defineAction({
  name: 'dev.issue.comment',
  method: 'post',
  path: '/dev/issues/{id}/comments',
  permission: D.keys.create,
  input: z.object({ id: Id, body: z.string().trim().min(1).max(65_000).describe('Markdown') }).strict(),
  output: IssueDetail,
  kind: 'external',
  summary: `Adds a comment to an issue. ${WRITE_NOTE}`,
});

export const updateIssueComment = defineAction({
  name: 'dev.issueComment.update',
  method: 'patch',
  path: '/dev/issue-comments/{id}',
  permission: D.keys.update,
  successStatus: 200,
  input: z.object({ id: Id, body: z.string().trim().min(1).max(65_000).describe('Markdown') }).strict(),
  output: IssueDetail,
  kind: 'external',
  summary: `Changes the text of a comment; Forgejo allows it for your own comments. ${WRITE_NOTE}`,
});

export const createMilestone = defineAction({
  name: 'dev.milestone.create',
  method: 'post',
  path: '/dev/milestones',
  permission: D.keys.create,
  input: z
    .object({
      repositoryId: Id,
      title: z.string().trim().min(1).max(255),
      description: z.string().max(10_000).default(''),
      dueOn: IsoDateTime.nullable().default(null),
    })
    .strict(),
  output: Milestone,
  kind: 'external',
  summary: `Creates a milestone in a mirrored repository. ${WRITE_NOTE}`,
});

export const updateMilestone = defineAction({
  name: 'dev.milestone.update',
  method: 'patch',
  path: '/dev/milestones/{id}',
  permission: D.keys.update,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      title: z.string().trim().min(1).max(255).optional(),
      description: z.string().max(10_000).optional(),
      dueOn: IsoDateTime.nullable().optional(),
      state: z.enum(['open', 'closed']).optional(),
    })
    .strict(),
  output: Milestone,
  kind: 'external',
  summary: `Changes a milestone: title, description, due date or state. ${WRITE_NOTE}`,
});

export const estimateIssue = defineAction({
  name: 'dev.issue.estimate',
  method: 'put',
  path: '/dev/issues/{id}/estimate',
  permission: D.keys.update,
  successStatus: 200,
  input: z.object({ id: Id, minutes: z.number().int().min(1).max(100_000).nullable().describe('null removes the estimate') }).strict(),
  output: IssueDetail,
  summary: 'Sets the estimate of an issue in minutes; an app field, not written to Forgejo (CO-0275)',
});

export const shareIssue = defineAction({
  name: 'dev.issue.share',
  method: 'put',
  path: '/dev/issues/{id}/client-visible',
  permission: D.keys.update,
  successStatus: 200,
  input: z.object({ id: Id, clientVisible: z.boolean() }).strict(),
  output: IssueDetail,
  summary: 'Shows an issue to the client in the portal, or hides it again; an app field, not written to Forgejo (CO-0665)',
});

export const ISSUE_SUGGESTABLE = ['labels', 'milestoneId'] as const;

export const suggestIssueFields = defineAction({
  name: 'dev.issue.suggest',
  method: 'post',
  path: '/dev/issues/{id}/suggestions',
  permission: D.keys.update,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      labels: z.object({ value: LabelNames.min(1), confidence: z.number().min(0).max(1), reason: z.string().max(500).default('') }).optional().describe('Labels to add, such as type/bug or severity/major'),
      milestoneId: z.object({ value: Id, confidence: z.number().min(0).max(1), reason: z.string().max(500).default('') }).optional(),
    })
    .strict(),
  output: IssueDetail,
  summary: 'Proposes labels and a milestone for an issue with how sure you are; an app field, a person accepts it and then it goes to Forgejo (CO-0845)',
});

export const dismissIssueSuggestion = defineAction({
  name: 'dev.issue.dismissSuggestion',
  method: 'delete',
  path: '/dev/issues/{id}/suggestions',
  permission: D.keys.update,
  successStatus: 200,
  input: z.object({ id: Id, field: z.enum(ISSUE_SUGGESTABLE).optional().describe('One field; leave out for all') }),
  output: IssueDetail,
  summary: 'Dismisses the proposal for a field of an issue, or all of them',
});

export const assignIssueAgent = defineAction({
  name: 'dev.issue.assignAgent',
  method: 'put',
  path: '/dev/issues/{id}/agent',
  permission: D.keys.update,
  successStatus: 200,
  input: z.object({ id: Id, agentId: Id.nullable().describe('null takes the agent off') }).strict(),
  output: IssueDetail,
  summary: 'Assigns an agent to an issue (CO-1236): an app field that starts a run of the agent on the issue',
  description: 'The agent reads the issue through MCP and writes progress and its result as comments; every comment is a draft a person approves. Not written to Forgejo.',
});

export const issueAgentAssigned = defineEvent({
  type: 'dev.issue.agentAssigned',
  version: 1,
  payload: z.object({ issueId: Id, agentId: Id, ref: z.string().describe('owner/name#number'), title: z.string(), assignedBy: Id }),
});

export const createIssueDependency = defineAction({
  name: 'dev.issueDependency.create',
  method: 'post',
  path: '/dev/issues/{id}/dependencies',
  permission: D.keys.update,
  successStatus: 200,
  input: z.object({ id: Id, blockerId: Id.describe('The mirrored issue that blocks this one') }).strict(),
  output: IssueDetail,
  kind: 'external',
  summary: `Makes an issue blocked by another mirrored issue. ${WRITE_NOTE}`,
});

export const deleteIssueDependency = defineAction({
  name: 'dev.issueDependency.delete',
  method: 'delete',
  path: '/dev/issues/{id}/dependencies',
  permission: D.keys.update,
  input: z.object({ id: Id, blocker: z.string().regex(/^[\w.-]+\/[\w.-]+#\d+$/).describe('owner/repo#12 of the blocker, as blockedBy lists it') }),
  output: IssueDetail,
  kind: 'external',
  summary: `Removes a blocker from an issue. ${WRITE_NOTE}`,
});

export const ActivityItem = z
  .object({
    source: z.enum(['forge', 'app']).describe('forge: the timeline of Forgejo; app: the audit trail of MKCore'),
    kind: z.string().describe('forge: label, milestone, assignees, close, reopen, change_title, commit_ref, pull_ref, issue_ref, review, merge_pull, pull_push, add_dependency, …; app: the action'),
    actor: z.string().nullable(),
    actorKind: z.enum(['human', 'agent', 'system']).nullable().describe('A person or an agent (CO-0551); for Forgejo through the linked account of the login, null when nobody linked it'),
    at: IsoDateTime,
    detail: z.record(z.string(), z.union([z.string(), z.boolean(), z.null()])),
  })
  .openapi('ActivityItem');

export const itemActivity = defineAction({
  name: 'dev.activity.list',
  method: 'get',
  path: '/dev/item-activity',
  permission: D.keys.read,
  input: z.object({ issueId: Id.optional(), pullRequestId: Id.optional() }),
  output: z.object({ items: z.array(ActivityItem), syncedAt: IsoDateTime.nullable().describe('When the timeline was last read from the forge') }),
  summary: 'The activity of an issue or pull request, newest first: the Forgejo timeline and the audit trail of the app (CO-0551)',
});

export const moveIssue = defineAction({
  name: 'dev.issue.move',
  method: 'post',
  path: '/dev/issues/{id}/move',
  permission: D.keys.update,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      group: z.string().min(1).max(100).describe('A label group a board may change, such as severity'),
      label: z.string().min(1).max(100).nullable().describe('The label of that group the issue gets, such as severity/major; null takes the group off'),
    })
    .strict(),
  output: IssueDetail,
  kind: 'external',
  summary: `Moves an issue on a label board: gives it one label of a group and takes the other labels of that group off. ${WRITE_NOTE}`,
});

export const bulkIssues = defineAction({
  name: 'dev.issue.bulk',
  method: 'post',
  path: '/dev/issues-bulk',
  permission: D.keys.update,
  successStatus: 200,
  input: z
    .object({
      ids: z.array(Id).min(1).max(100),
      state: z.enum(['open', 'closed']).optional(),
      addLabels: LabelNames.default([]),
      removeLabels: LabelNames.default([]),
      assignees: z.array(Login).max(10).optional().describe('The full list of assignees for every issue'),
    })
    .strict(),
  output: z.object({ results: z.array(z.object({ id: Id, ok: z.boolean(), code: z.string().nullable(), detail: z.string().nullable() })) }),
  kind: 'external',
  summary: `Changes state, labels or assignees of up to a hundred issues at once; the answer says per issue whether it worked. ${WRITE_NOTE}`,
});

export const issueChanged = defineEvent({
  type: 'dev.issue.changed',
  version: 1,
  payload: z.object({ issueId: Id, repositoryId: Id, change: z.enum(['created', 'updated', 'labels', 'commented', 'comment']) }),
});
/** A new comment on an issue, with the logins it mentions, for routines that react to it (CO-1249). */
export const issueCommented = defineEvent({
  type: 'dev.issue.commented',
  version: 1,
  payload: z.object({ issueId: Id, repositoryId: Id, ref: z.string().describe('owner/repo#12'), author: z.string().nullable(), mentions: z.array(z.string()) }),
});
/** A new comment on a pull request, from the webhook, with the logins it mentions (CO-1249). */
export const pullCommented = defineEvent({
  type: 'dev.pr.commented',
  version: 1,
  payload: z.object({ pullRequestId: Id.nullable(), repositoryId: Id, ref: z.string().describe('owner/repo#12'), author: z.string().nullable(), mentions: z.array(z.string()) }),
});
/** What a webhook delivery about a pull request says happened to it (CO-1399). */
export const PULL_CHANGES = ['opened', 'updated', 'merged', 'closed', 'reviewed', 'ci'] as const;
/**
 * A pull request changed in the forge, from the webhook, for routines that react to it (CO-1399): opened, updated
 * (new commits, labels, assignees, edits), merged, closed, reviewed, or a new CI state of its head (ciState). One
 * event per delivery and pull request; deliveryId is the id of the Forgejo delivery.
 */
export const pullChanged = defineEvent({
  type: 'dev.pr.changed',
  version: 1,
  payload: z.object({
    pullRequestId: Id,
    repositoryId: Id,
    number: z.number().int(),
    change: z.enum(PULL_CHANGES),
    ciState: z.enum(['pending', 'success', 'failure']).nullable().optional().describe('The combined CI state of the head; with change ci'),
    branch: z.string().nullable().describe('The head branch of the pull request'),
    deliveryId: z.string(),
  }),
});
/** A push to a branch of a mirrored repository, from the webhook (CO-1399); head is the new head commit, commits the number pushed. */
export const pushReceived = defineEvent({
  type: 'dev.push.received',
  version: 1,
  payload: z.object({ repositoryId: Id, branch: z.string(), head: z.string(), commits: z.number().int(), deliveryId: z.string() }),
});
export const milestoneChanged = defineEvent({
  type: 'dev.milestone.changed',
  version: 1,
  payload: z.object({ milestoneId: Id, repositoryId: Id, change: z.enum(['created', 'updated']) }),
});

// ---------------------------------------------------------------- pull requests and CI (CO-0420, CO-0421, CO-0416, CO-0422)

export const PULL_STATES = ['open', 'closed', 'merged', 'all'] as const;
export const PULL_INBOXES = ['review', 'mine', 'unreviewed'] as const;

export const PullRequest = z
  .object({
    id: Id,
    repositoryId: Id,
    repository: z.string().describe('owner/name'),
    number: z.number().int(),
    title: z.string(),
    state: z.enum(['open', 'closed', 'merged']),
    draft: z.boolean(),
    author: z.string().nullable(),
    headRef: z.string().nullable(),
    baseRef: z.string().nullable(),
    labels: z.array(z.string()),
    assignees: z.array(z.string()),
    requestedReviewers: z.array(z.string()),
    approvals: z.number().int().describe('Approving reviews that are not dismissed'),
    changesRequested: z.boolean().describe('The latest verdict of some reviewer is request changes'),
    ciState: z.enum(CI_STATES).nullable().describe('Combined CI state of the head commit; null when there is none'),
    mergeable: z.boolean().nullable().describe('An open pull request merges into its base without conflicts; false when it has conflicts, null when it is not open or not known yet'),
    commentCount: z.number().int(),
    url: z.string(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    mergedAt: IsoDateTime.nullable(),
    closedAt: IsoDateTime.nullable(),
  })
  .openapi('PullRequest');

export const PullReview = z
  .object({
    id: Id,
    reviewer: z.string().nullable(),
    state: z.enum(['approved', 'changes_requested', 'commented', 'pending', 'requested', 'dismissed']),
    body: z.string(),
    stale: z.boolean(),
    submittedAt: IsoDateTime.nullable(),
  })
  .openapi('PullReview');

export const CiStatus = z
  .object({
    context: z.string(),
    state: z.enum(['pending', 'success', 'failure', 'error', 'warning']),
    description: z.string(),
    targetUrl: z.string().nullable(),
    updatedAt: IsoDateTime.nullable(),
  })
  .openapi('CiStatus');

export const PullDetail = PullRequest.extend({
  body: z.string(),
  headSha: z.string().nullable(),
  mergedBy: z.string().nullable(),
  mergeCommitSha: z.string().nullable(),
  reviews: z.array(PullReview),
  statuses: z.array(CiStatus),
  commits: z.number().int().describe('Commits of the pull request known to the mirror (merged pull requests)'),
  issues: z.array(z.object({ id: Id, number: z.number().int(), title: z.string(), state: z.enum(['open', 'closed']), kind: z.enum(['closes', 'mentions']) })),
  syncedAt: IsoDateTime,
}).openapi('PullDetail');

export const listPulls = defineAction({
  name: 'dev.pr.list',
  method: 'get',
  path: '/dev/pulls',
  permission: D.keys.read,
  input: ListQuery.extend({
    repositoryId: Id.optional(),
    projectId: Id.optional(),
    organisationId: Id.optional(),
    state: z.enum(PULL_STATES).default('open'),
    inbox: z
      .enum(PULL_INBOXES)
      .optional()
      .describe('review: your review is requested; mine: your open pull requests; unreviewed: open, not draft, without an approval. You are your linked Forgejo account'),
    author: z.string().max(100).optional(),
    q: z.string().max(200).optional().describe('Words in title or body, or #number'),
  }),
  output: Paginated(PullRequest),
  summary: 'Mirrored pull requests over all repositories, the most recently updated first, with reviews and CI in short',
});

export const getPull = defineAction({
  name: 'dev.pr.get',
  method: 'get',
  path: '/dev/pulls/{id}',
  permission: D.keys.read,
  input: z.object({ id: Id }),
  output: PullDetail,
});

export const mergePull = defineAction({
  name: 'dev.pr.merge',
  method: 'post',
  path: '/dev/pulls/{id}/merge',
  permission: D.keys.approve,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      method: z.enum(['merge', 'rebase', 'squash']).default('merge'),
      message: z.string().max(10_000).optional().describe('The merge commit message; default the one Forgejo makes'),
      deleteBranch: z.boolean().default(false).describe('Delete the head branch after the merge'),
    })
    .strict(),
  output: PullDetail,
  kind: 'external',
  summary: 'Merges a reviewed pull request in Forgejo with your own linked account, only while its head is the commit you saw; a person only, never an agent (CO-1238)',
});

export const REVIEW_EVENTS = ['approve', 'request_changes', 'comment'] as const;

export const reviewPull = defineAction({
  name: 'dev.pr.review',
  method: 'post',
  path: '/dev/pulls/{id}/reviews',
  permission: D.keys.update,
  successStatus: 200,
  input: z
    .object({
      id: Id,
      event: z.enum(REVIEW_EVENTS).describe('approve needs dev:approve and is for a person only; request_changes and comment need a body or line comments'),
      body: z.string().max(50_000).default('').describe('The review, in markdown'),
      comments: z
        .array(z.object({ path: z.string().min(1).max(500).describe('The file, as in the diff'), line: z.number().int().positive().describe('The line in the new version of the file'), body: z.string().min(1).max(10_000) }).strict())
        .max(50)
        .default([])
        .describe('Comments on lines of the diff'),
    })
    .strict(),
  output: PullDetail,
  kind: 'external',
  summary: 'Reviews a pull request in Forgejo: approve, request changes or comment, with comments on lines; on the head the mirror knows (CO-0816)',
  description:
    'A person reviews with their own linked account. An agent proposes a review (comment or request changes) as a draft a person approves; it is then written with the bot account of the agent. ' +
    'An agent never approves a pull request. Not for drafts, closed or merged pull requests.',
});

export const ciStatus = defineAction({
  name: 'dev.ci.status',
  method: 'get',
  path: '/dev/ci-status',
  permission: D.keys.read,
  input: z.object({
    pullRequestId: Id.optional().describe('The head commit of this pull request'),
    repositoryId: Id.optional(),
    sha: z.string().regex(/^[0-9a-f]{7,64}$/i).optional().describe('A commit of repositoryId'),
  }),
  output: z.object({ sha: z.string().nullable(), state: z.enum(CI_STATES).nullable(), statuses: z.array(CiStatus), checkedAt: IsoDateTime.nullable() }),
  summary: 'The CI state of a pull request or commit from the mirror (Woodpecker reports to Forgejo), with every context and its link',
});

// ---------------------------------------------------------------- activity and compliance (CO-0417, CO-0418)

const Window = z.coerce.number().int().pipe(z.union([z.literal(7), z.literal(30), z.literal(90)])).default(30).describe('Days back: 7, 30 or 90');
const InsightScope = { repositoryId: Id.optional(), organisationId: Id.optional().describe('A client'), projectId: Id.optional() };

export const Identities = z
  .object({
    aliases: z.array(z.object({ canonical: z.string().min(1).max(100), alts: z.array(z.string().min(1).max(200)).max(50) })).max(200),
    bots: z.array(z.string().min(1).max(100)).max(100).describe('Patterns: an author whose login, name or address equals or contains one is a bot'),
  })
  .openapi('Identities');

export const getIdentities = defineAction({
  name: 'dev.identity.get',
  method: 'get',
  path: '/dev/identities',
  permission: D.keys.read,
  input: z.object({}),
  output: Identities,
  summary: 'Identity aliases (one person behind several logins and addresses) and bot patterns for commit activity',
});

export const updateIdentities = defineAction({
  name: 'dev.identity.update',
  method: 'put',
  path: '/dev/identities',
  permission: D.keys.manage,
  successStatus: 200,
  input: Identities.strict(),
  output: Identities,
});

export const commitActivity = defineAction({
  name: 'dev.commit.activity',
  method: 'get',
  path: '/dev/commit-activity',
  permission: D.keys.read,
  input: z.object({ days: Window, who: z.enum(['all', 'humans', 'bots']).default('all'), ...InsightScope }),
  output: z.object({
    since: IsoDateTime,
    days: z.array(z.string()).describe('The days of the window, YYYY-MM-DD in Europe/Amsterdam'),
    uncertain: z.boolean().describe('Some repository is not fully synced: the numbers may be low'),
    people: z.array(
      z.object({
        key: z.string(),
        name: z.string(),
        bot: z.boolean(),
        total: z.number().int(),
        perDay: z.array(
          z.object({
            day: z.string(),
            n: z.number().int(),
            firstAt: IsoDateTime.describe('The first commit of the day (#1336)'),
            lastAt: IsoDateTime.describe('The last commit of the day'),
            repositories: z
              .array(z.object({ repositoryId: Id, fullName: z.string(), n: z.number().int(), firstAt: IsoDateTime, lastAt: IsoDateTime }))
              .describe('The commits of the day per repository, for time from commits (CO-0250)'),
          }),
        ),
      }),
    ),
    repositories: z.array(z.object({ repositoryId: Id, fullName: z.string(), commits: z.number().int(), people: z.number().int(), lastCommitAt: IsoDateTime.nullable(), uncertain: z.boolean() })),
  }),
  summary: 'Commits on the default branches per person per day (identity aliases applied, bots marked) and per repository',
});

export const complianceReport = defineAction({
  name: 'dev.compliance.report',
  method: 'get',
  path: '/dev/compliance',
  permission: D.keys.read,
  input: z.object({ days: Window, repositoryId: Id.optional(), organisationId: Id.optional().describe('A client') }),
  output: z.object({
    since: IsoDateTime,
    uncertain: z.boolean(),
    totals: z.object({ directPushes: z.number().int(), mergesWithoutReview: z.number().int() }),
    repositories: z.array(
      z.object({
        repositoryId: Id,
        fullName: z.string(),
        status: z.enum(['ok', 'uncertain']),
        reason: z.string().nullable().describe('backfill, sync-error, sync-uncertain or unchecked-commits'),
        directPushes: z.number().int().nullable().describe('null: unknown, never a false 0'),
        mergesWithoutReview: z.number().int().nullable(),
        uncheckedCommits: z.number().int(),
      }),
    ),
    findings: z.array(
      z.object({
        kind: z.enum(['direct_push', 'merge_without_review']),
        repositoryId: Id,
        repository: z.string(),
        ref: z.string(),
        title: z.string(),
        actor: z.string().nullable(),
        at: IsoDateTime,
        url: z.string(),
      }),
    ),
  }),
  summary: 'Direct pushes to the default branch and merges without an approval by someone else, per repository; unknown instead of a false 0',
});

export const repositorySynced = defineEvent({
  type: 'dev.repository.synced',
  version: 1,
  payload: z.object({ repositoryId: Id, kind: z.enum(['backfill', 'reconcile', 'full', 'webhook']), issues: z.number().int(), comments: z.number().int() }),
});
export const repositoryChanged = defineEvent({
  type: 'dev.repository.changed',
  version: 1,
  payload: z.object({ repositoryId: Id, change: z.enum(['added', 'enabled', 'disabled', 'updated', 'deleted']) }),
});
export const mirrorEvents = [repositorySynced, repositoryChanged, issueChanged, issueCommented, pullCommented, pullChanged, pushReceived, milestoneChanged, issueAgentAssigned];

// ---------------------------------------------------------------- pilot activity (CO-1368)

/** The kinds of external feedback source; one adapter each in packages/integrations (havep: the HAVEP ERP API). */
export const FEEDBACK_SOURCE_KINDS = ['havep'] as const;
/** The secret per client that holds the key of its feedback source (Settings, Secrets, with the client). */
export const FEEDBACK_SOURCE_SECRET = 'feedback.apiKey';

export const ExternalFeedbackSource = z
  .object({
    id: Id,
    organisationId: Id,
    kind: z.enum(FEEDBACK_SOURCE_KINDS),
    name: z.string(),
    url: z.string().describe('The GraphQL endpoint of the source'),
    externalId: z.string().describe('The id of the client in the source, such as the companyId of HAVEP'),
    startsOn: IsoDate.describe('The first day of week 1 of the pilot; weeks run seven days from this weekday'),
    excludedEmails: z.array(z.string()).describe('Accounts that are not the client (staff, bots): left out of the activity'),
    enabled: z.boolean(),
    hasKey: z.boolean().describe(`Whether the secret ${FEEDBACK_SOURCE_SECRET} is set for the client`),
    lastSyncedAt: IsoDateTime.nullable(),
    lastError: z.string().nullable(),
    version: z.number().int(),
  })
  .openapi('ExternalFeedbackSource');

const sourceFields = {
  name: z.string().trim().min(1).max(200),
  url: z.url().max(500),
  externalId: z.string().trim().min(1).max(200),
  startsOn: IsoDate,
  excludedEmails: z.array(z.string().trim().toLowerCase().min(1).max(320)).max(200),
};

export const listFeedbackSources = defineAction({
  name: 'dev.feedbackSource.list',
  method: 'get',
  path: '/dev/feedback-sources',
  permission: D.keys.read,
  input: z.object({ organisationId: Id.optional() }),
  output: z.object({ items: z.array(ExternalFeedbackSource) }),
  summary: 'The external feedback sources per client, such as the HAVEP feedback API, with when they last synced (CO-1368)',
});

export const createFeedbackSource = defineAction({
  name: 'dev.feedbackSource.create',
  method: 'post',
  path: '/dev/feedback-sources',
  permission: D.keys.manage,
  input: z.object({ organisationId: Id, kind: z.enum(FEEDBACK_SOURCE_KINDS).default('havep'), ...sourceFields, excludedEmails: sourceFields.excludedEmails.default([]) }).strict(),
  output: ExternalFeedbackSource,
  summary: `Adds a feedback source for a client; its key goes in the secret ${FEEDBACK_SOURCE_SECRET} of that client (CO-1368)`,
});

export const updateFeedbackSource = defineAction({
  name: 'dev.feedbackSource.update',
  method: 'patch',
  path: '/dev/feedback-sources/{id}',
  permission: D.keys.manage,
  input: z.object({ id: Id, ...z.object(sourceFields).partial().shape, enabled: z.boolean().optional(), expectedVersion: z.number().int().positive().optional() }).strict(),
  output: ExternalFeedbackSource,
  etag: true,
  summary: 'Changes a feedback source, or switches it off',
});

export const deleteFeedbackSource = defineAction({
  name: 'dev.feedbackSource.delete',
  method: 'delete',
  path: '/dev/feedback-sources/{id}',
  permission: D.keys.manage,
  input: z.object({ id: Id }),
  output: z.object({ deleted: z.boolean() }),
  successStatus: 200,
  kind: 'destructive',
  summary: 'Removes a feedback source with the activity it fetched',
});

export const syncFeedbackSource = defineAction({
  name: 'dev.feedbackSource.sync',
  method: 'post',
  path: '/dev/feedback-sources/{id}/sync',
  permission: D.keys.manage,
  successStatus: 202,
  input: z.object({ id: Id }).strict(),
  output: z.object({ workflowId: z.string() }),
  summary: 'Fetches the activity of a feedback source now, for the weeks it lacks (the daily job does the same)',
});

export const PilotWeek = z.object({ week: IsoDate, activeDays: z.number().int(), sessions: z.number().int(), feedback: z.number().int() });

export const listPilotActivity = defineAction({
  name: 'dev.pilotActivity.list',
  method: 'get',
  path: '/dev/pilot-activity',
  permission: D.keys.read,
  input: z.object({ organisationId: Id, weeks: z.coerce.number().int().min(1).max(52).default(8).describe('The last weeks to show, up to the current one') }),
  output: z
    .object({
      sources: z.array(z.object({ id: Id, name: z.string(), lastSyncedAt: IsoDateTime.nullable(), lastError: z.string().nullable() })),
      weeks: z.array(z.object({ week: IsoDate, number: z.number().int().describe('Pilot week, counted from the start date'), activeUsers: z.number().int(), sessions: z.number().int(), feedback: z.number().int() })),
      users: z.array(
        z.object({
          externalUserId: z.string(),
          name: z.string().nullable(),
          email: z.string().nullable(),
          lastActiveAt: IsoDateTime.nullable(),
          feedbackTotal: z.number().int(),
          cells: z.array(PilotWeek),
        }),
      ),
    })
    .openapi('PilotActivity'),
  summary: 'How the users of a client use its system per week: active days, sessions and feedback per user, from the feedback source (CO-1368)',
});

